import React from "react";
import ReactDOM from "react-dom/client";
import { ClerkProvider } from "@clerk/clerk-react";
import App from "./App";
import { AppProviders } from "./contexts/AppProviders";

// In non-prod, the Clerk publishable key comes from the environment switcher
// config (per-account Clerk apps). In prod, it's baked in at build time.
import environments from "./constants/environments.json";
import { getApiEnvironment, isNonProdEnvironment } from "./constants/api.constants";

function resolveClerkKey() {
  if (isNonProdEnvironment()) {
    const env = getApiEnvironment();
    const config = environments[env];
    if (config?.clerkPublishableKey) return config.clerkPublishableKey;
  }
  return import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
}

const CLERK_PUBLISHABLE_KEY = resolveClerkKey();

const root = ReactDOM.createRoot(document.getElementById("root"));

const isValidKey = CLERK_PUBLISHABLE_KEY && CLERK_PUBLISHABLE_KEY.startsWith('pk_');

if (!isValidKey) {
  // Invalid or missing key — show environment selector so user can switch
  const EnvironmentSwitcher = React.lazy(() => import("./components/EnvironmentSwitcher"));
  const currentEnv = getApiEnvironment();
  root.render(
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem", fontFamily: "system-ui, -apple-system, sans-serif", background: "#111", color: "#fff" }}>
      <div style={{ maxWidth: 640, border: "1px solid #555", padding: "2rem", borderRadius: 8 }}>
        <h1 style={{ marginTop: 0 }}>Select Environment</h1>
        <p>Current environment "{currentEnv}" has no valid Clerk key configured. Select a different environment:</p>
        <React.Suspense fallback={<p>Loading...</p>}>
          <EnvironmentSwitcher open={true} onClose={() => {}} />
        </React.Suspense>
      </div>
    </div>
  );
} else {
  root.render(
    <React.StrictMode>
      {/*
        clerkJSVersion pinned to a specific version (not the floating "5" major)
        because Clerk's CDN responds to "5" with a 307 redirect to the latest
        patch — and modern browsers refuse to follow redirects on script
        preflight requests, so the SDK never loads from custom domains
        (equaliq.app). Pinning bypasses the redirect entirely. Bump when a
        newer @clerk/clerk-react expects newer clerk-js.
      */}
      <ClerkProvider
        publishableKey={CLERK_PUBLISHABLE_KEY}
        clerkJSVersion="5.125.9"
      >
        <AppProviders>
          <App />
        </AppProviders>
      </ClerkProvider>
    </React.StrictMode>
  );
}

// E2E readiness signal — Playwright tests at frontend/e2e/ wait on this flag
// instead of `networkidle` (which a React 19 + SWR app never reaches because
// SWR keeps polling). Set after a microtask so React mounts first.
queueMicrotask(() => {
  if (typeof window !== "undefined") {
    window.__EIQ_READY__ = true;
  }
});